import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { MapContainer, TileLayer, CircleMarker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Eye } from "lucide-react";
import { useUpdateClient, useRevealPortalCredential, ClientRecord, PortalName, PORTAL_LABEL } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";

interface FormValues {
  fullName?: string;
  businessName?: string;
  tradeName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  city?: string;
  state?: string;
  address?: string;
  economicActivity?: string;
  ciiuCode?: string;
  taxRegime?: string;
  status: ClientRecord["status"];
  notes?: string;
}

const BOGOTA_CENTER: [number, number] = [4.65, -74.1];

/** Captura el clic dentro del mapa y lo entrega como la coordenada elegida — es lo que convierte el mapa en un "picker" en vez de solo mostrar un punto fijo. */
function LocationPicker({ onPick }: { onPick: (pos: [number, number]) => void }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

interface PortalCredsState {
  dianUsername: string;
  dianPassword: string;
  camaraComercioUsername: string;
  camaraComercioPassword: string;
  icaUsername: string;
  icaPassword: string;
}

const PORTAL_FIELD_KEYS: Record<PortalName, { username: keyof PortalCredsState; password: keyof PortalCredsState }> = {
  DIAN: { username: "dianUsername", password: "dianPassword" },
  CAMARA_COMERCIO: { username: "camaraComercioUsername", password: "camaraComercioPassword" },
  ICA: { username: "icaUsername", password: "icaPassword" },
};

export default function EditClientModal({ client, onClose }: { client: ClientRecord; onClose: () => void }) {
  const { register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      fullName: client.fullName ?? "",
      businessName: client.businessName ?? "",
      tradeName: client.tradeName ?? "",
      email: client.email ?? "",
      phone: client.phone ?? "",
      mobile: client.mobile ?? "",
      city: client.city ?? "",
      state: client.state ?? "",
      address: client.address ?? "",
      economicActivity: client.economicActivity ?? "",
      ciiuCode: client.ciiuCode ?? "",
      taxRegime: client.taxRegime ?? "",
      status: client.status,
      notes: client.notes ?? "",
    },
  });
  const updateClient = useUpdateClient();
  const revealCredential = useRevealPortalCredential();

  // Ubicación en el mapa (módulo Operación) — a pedido explícito del
  // usuario. Manual siempre: no hay geocodificación automática configurada
  // (regla #2, honestidad sobre integraciones), así que el punto solo queda
  // registrado si alguien lo marca a mano acá.
  const [position, setPosition] = useState<[number, number] | null>(
    client.latitude != null && client.longitude != null ? [Number(client.latitude), Number(client.longitude)] : null
  );

  // Accesos a portales del Estado (DIAN/Cámara de Comercio/ICA) — la
  // contraseña se guarda cifrada en el backend y nunca viaja de vuelta salvo
  // que se pida explícitamente con "Ver" (revealCredential), que queda
  // auditado. Dejar el campo en blanco al guardar significa "no tocar la
  // contraseña ya guardada", nunca "borrarla" (ver client.service.ts).
  const [portalCreds, setPortalCreds] = useState<PortalCredsState>({
    dianUsername: client.dianUsername ?? "",
    dianPassword: "",
    camaraComercioUsername: client.camaraComercioUsername ?? "",
    camaraComercioPassword: "",
    icaUsername: client.icaUsername ?? "",
    icaPassword: "",
  });
  const [revealed, setRevealed] = useState<Partial<Record<PortalName, string>>>({});

  const portalPasswordSet: Record<PortalName, boolean> = {
    DIAN: client.dianPasswordSet,
    CAMARA_COMERCIO: client.camaraComercioPasswordSet,
    ICA: client.icaPasswordSet,
  };

  const handleReveal = async (portal: PortalName) => {
    try {
      const result = await revealCredential.mutateAsync({ clientId: client.id, portal });
      setRevealed((prev) => ({ ...prev, [portal]: result.password ?? "" }));
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      await updateClient.mutateAsync({
        id: client.id,
        ...values,
        latitude: position ? position[0] : null,
        longitude: position ? position[1] : null,
        dianUsername: portalCreds.dianUsername,
        dianPassword: portalCreds.dianPassword || undefined,
        camaraComercioUsername: portalCreds.camaraComercioUsername,
        camaraComercioPassword: portalCreds.camaraComercioPassword || undefined,
        icaUsername: portalCreds.icaUsername,
        icaPassword: portalCreds.icaPassword || undefined,
      });
      toast.success("Cliente actualizado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900">Editar cliente</h2>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {client.clientType === "PERSONA_NATURAL" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre completo</label>
              <input className="input-field mt-1" {...register("fullName")} />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Razón social</label>
                <input className="input-field mt-1" {...register("businessName")} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre comercial</label>
                <input className="input-field mt-1" {...register("tradeName")} />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Correo</label>
              <input type="email" className="input-field mt-1" {...register("email")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Teléfono</label>
              <input className="input-field mt-1" {...register("phone")} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Ciudad</label>
              <input className="input-field mt-1" {...register("city")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Departamento</label>
              <input className="input-field mt-1" {...register("state")} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Régimen tributario</label>
            <input className="input-field mt-1" {...register("taxRegime")} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Estado</label>
            <select className="input-field mt-1" {...register("status")}>
              <option value="ACTIVE">Activo</option>
              <option value="PROSPECT">Prospecto</option>
              <option value="INACTIVE">Inactivo</option>
              <option value="ARCHIVED">Archivado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas internas</label>
            <textarea className="input-field mt-1" rows={3} {...register("notes")} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Ubicación en el mapa</label>
            <p className="mt-0.5 text-xs text-gray-400">Haz clic en el mapa para marcar dónde queda este cliente (aparece en el módulo Operación).</p>
            <div className="mt-2 overflow-hidden rounded-md border border-gray-200" style={{ height: 200 }}>
              <MapContainer center={position ?? BOGOTA_CENTER} zoom={position ? 14 : 6} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {position && (
                  <CircleMarker center={position} radius={8} pathOptions={{ color: "#2f5488", fillColor: "#2f5488", fillOpacity: 0.85 }} />
                )}
                <LocationPicker onPick={setPosition} />
              </MapContainer>
            </div>
            {position && (
              <div className="mt-1.5 flex items-center justify-between text-xs text-gray-500">
                <span>
                  Lat {position[0].toFixed(5)}, Lng {position[1].toFixed(5)}
                </span>
                <button type="button" className="text-red-500 hover:text-red-700" onClick={() => setPosition(null)}>
                  Quitar ubicación
                </button>
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-md border border-gray-100 p-3">
            <div>
              <p className="text-sm font-medium text-gray-700">Accesos a portales del Estado</p>
              <p className="text-xs text-gray-400">La contraseña queda cifrada. Déjala en blanco para no cambiar la que ya está guardada.</p>
            </div>
            {(Object.keys(PORTAL_LABEL) as PortalName[]).map((portal) => {
              const { username: usernameKey, password: passwordKey } = PORTAL_FIELD_KEYS[portal];
              const passwordSet = portalPasswordSet[portal];
              return (
                <div key={portal} className="space-y-1.5">
                  <p className="text-xs font-semibold text-gray-500">{PORTAL_LABEL[portal]}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="input-field text-xs"
                      placeholder="Usuario"
                      value={portalCreds[usernameKey]}
                      onChange={(e) => setPortalCreds((p) => ({ ...p, [usernameKey]: e.target.value }))}
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        className="input-field flex-1 text-xs"
                        placeholder={passwordSet ? "•••••••• (guardada)" : "Contraseña"}
                        value={revealed[portal] ?? portalCreds[passwordKey]}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPortalCreds((p) => ({ ...p, [passwordKey]: value }));
                          setRevealed((r) => {
                            if (!(portal in r)) return r;
                            const next = { ...r };
                            delete next[portal];
                            return next;
                          });
                        }}
                      />
                      {passwordSet && (
                        <button
                          type="button"
                          className="shrink-0 text-gray-400 hover:text-brand-600 disabled:opacity-50"
                          title="Ver contraseña guardada"
                          onClick={() => handleReveal(portal)}
                          disabled={revealCredential.isPending}
                        >
                          <Eye size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={updateClient.isPending}>
              {updateClient.isPending ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
